import { useState } from "react";
import PropTypes from "prop-types";

import { Button } from "@/components/ui/button";
import { Spinner } from "@/components/ui/spinner";
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from "@/components/ui/tooltip";
import { getTranscriptUnavailableMessage } from "components/CallTranscript/transcriptUnavailability";
import TextContentDialog from "components/LogItem/elements/TextContentDialog";
import ViewCallTranscriptDialog from "components/modals/ViewCallTranscriptDialog";
import useRequestTranscription from "hooks/useRequestTranscription";
import callConstants from "constants/Call";

const WITHOUT_AFFORDANCE = new Set(
  callConstants.TRANSCRIPT_STATUSES_WITHOUT_AFFORDANCE
);
const IN_PROGRESS = new Set(callConstants.TRANSCRIPT_STATUSES_IN_PROGRESS);

/**
 * A disabled Button has `pointer-events: none`, so it never becomes the hover
 * target. The wrapper is what receives the mouse.
 *
 * There is no message for a readable transcript, and a call reaches the
 * disabled branch on a missing id alone, so the button is returned bare rather
 * than under an empty tooltip.
 */
function withTooltip(button, message) {
  if (!message) {
    return button;
  }

  return (
    <Tooltip>
      <TooltipTrigger render={<span className="tw:inline-block" />}>
        {button}
      </TooltipTrigger>
      <TooltipContent>{message}</TooltipContent>
    </Tooltip>
  );
}

/**
 * View Transcript button for an activity-log call entry.
 *
 * Two unrelated things are called a transcript on a call: the machine
 * transcription of its recording, and the free text an agent may type when
 * logging a call by hand. Only ever one button is rendered, preferring the
 * machine transcription, so an entry never shows the same label twice.
 *
 * A recording nobody has transcribed yet offers to start one instead of
 * showing a button that cannot be pressed. Every remaining state that leaves
 * the button unusable carries a tooltip saying which one it is, because
 * several unrelated situations otherwise look like one fault.
 */
export function CallTranscriptButton({
  callId,
  transcriptStatus,
  transcriptSkipReason,
  hasRecording,
  manualTranscript,
  onRequested,
}) {
  const [open, setOpen] = useState(false);
  const { mutate: requestTranscription, isPending } = useRequestTranscription({
    onAnswered: onRequested,
  });

  const hasTranscription =
    !!callId && !!transcriptStatus && !WITHOUT_AFFORDANCE.has(transcriptStatus);
  const unavailableMessage = getTranscriptUnavailableMessage({
    transcriptStatus,
    transcriptSkipReason,
    hasRecording,
  });
  // A request by hand overrides the rules that skipped the recording, so a
  // skipped transcript can still be asked for. A failed one is terminal.
  const canRequest =
    !!callId &&
    hasRecording &&
    (!transcriptStatus ||
      transcriptStatus === callConstants.TRANSCRIPT_STATUSES.SKIPPED);

  if (hasTranscription) {
    // The calls table shows the same in-progress state for these statuses, and
    // the two surfaces are meant to read alike.
    const inProgress = IN_PROGRESS.has(transcriptStatus);

    const button = (
      <Button
        variant="outline"
        size="sm"
        disabled={inProgress}
        aria-busy={inProgress || undefined}
        onClick={() => setOpen(true)}
      >
        {inProgress && <Spinner data-icon="inline-start" />}
        View Transcript
      </Button>
    );

    return (
      <>
        {inProgress ? withTooltip(button, unavailableMessage) : button}
        <ViewCallTranscriptDialog
          callId={callId}
          open={open}
          onOpenChange={setOpen}
        />
      </>
    );
  }

  if (manualTranscript) {
    return (
      <TextContentDialog label="View Transcript" title="Transcript">
        {manualTranscript}
      </TextContentDialog>
    );
  }

  if (canRequest) {
    return (
      <Button
        variant="outline"
        size="sm"
        disabled={isPending}
        aria-busy={isPending || undefined}
        onClick={() => requestTranscription(callId)}
      >
        {isPending && <Spinner data-icon="inline-start" />}
        Request Transcription
      </Button>
    );
  }

  return withTooltip(
    <Button variant="outline" size="sm" disabled>
      View Transcript
    </Button>,
    unavailableMessage
  );
}

CallTranscriptButton.propTypes = {
  callId: PropTypes.number,
  transcriptStatus: PropTypes.string,
  transcriptSkipReason: PropTypes.string,
  hasRecording: PropTypes.bool,
  manualTranscript: PropTypes.string,
  onRequested: PropTypes.func,
};

export default CallTranscriptButton;
